
import { SEO } from '../components/SEO';

export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEO 
        title="Privacy Policy"
        description="Read our Privacy Policy to understand how we collect, use, and protect your data at Atelier Persopol."
        url="https://www.atelier-persopol.com/privacy"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-8">Privacy Policy for Atelier Persopol</h1>
          
          <p className="text-gray-600 mb-6">Last updated: 2025-Dec-19</p>
          
          <p className="text-gray-600 mb-6">
            Atelier Persopol OÜ (“we”, “our”, “us”) operates the mobile application Atelier Persopol (the “App”) and related services that transform user photos into personalized artistic products.
          </p>
          
          <p className="text-gray-600 mb-6">
            We are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our App or visit our website.
          </p>
          
          <p className="text-gray-600 mb-8">
            By using our App, you agree to the practices described in this Privacy Policy.
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">1.1 Photos & Uploaded Content</h3>
            <p className="text-gray-600 mb-4">
              When you upload photos or images to generate artwork, these files are processed by our servers for the sole purpose of producing the requested outputs.
            </p>
            <p className="text-gray-600 mb-4">
              We do not use your photos to train AI models unless you explicitly opt-in.
            </p>
            <p className="text-gray-600 mb-6">
              Photos may be stored temporarily to generate your products or saved in your account only if you choose.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">1.2 Face Data</h3>
            <p className="text-gray-600 mb-4">
              When you upload photos containing faces, we collect the face data within these images.
            </p>
            <ul className="list-disc pl-5 mb-6 text-gray-600 space-y-1">
              <li><strong>Use:</strong> We use the collected face data only for image transformation to generate the personalized artistic products you request.</li>
              <li><strong>Sharing:</strong> Face data is not shared with third parties, except for our AI processing service providers solely to perform the image transformation and cloud storage service provider aimed to securely store the data. We do not sell or trade face data.</li>
              <li><strong>Retention:</strong> The original image containing face data is stored until you decide to delete your account. You can manually trigger the deletion of your account and all associated data at any time.</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">1.3 Personal Information</h3>
            <p className="text-gray-600 mb-2">We may collect:</p>
            <ul className="list-disc pl-5 mb-6 text-gray-600 space-y-1">
              <li>Name (if provided)</li>
              <li>Email address</li>
              <li>Shipping address (only when ordering physical products)</li>
              <li>Payment information (processed securely by third-party providers; we do not store credit card details)</li>
              <li>Account credentials (if you create an account)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">1.4 Usage Data</h3>
            <p className="text-gray-600 mb-2">We automatically collect:</p>
            <ul className="list-disc pl-5 mb-6 text-gray-600 space-y-1">
              <li>Device information (model, OS version)</li>
              <li>App activity and interactions</li>
              <li>IP address</li>
              <li>Analytics for improving our services</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">1.5 Cookies & Tracking</h3>
            <p className="text-gray-600 mb-2">
              Our website may use cookies to improve user experience and analytics.
            </p>
            <p className="text-gray-600 mb-4">
              The App itself does not use third-party tracking without explicit consent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h2>
            <p className="text-gray-600 mb-2">We use the collected data to:</p>
            <ul className="list-disc pl-5 mb-4 text-gray-600 space-y-1">
              <li>Generate AI-powered artwork from your photos</li>
              <li>Provide customized products and process orders</li>
              <li>Improve and optimize the App experience</li>
              <li>Communicate with you (support, updates, promotions)</li>
              <li>Prevent fraud and maintain security</li>
              <li>Comply with legal obligations</li>
            </ul>
            <p className="text-gray-600 mb-4">We never sell your data.</p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Share Your Information</h2>
            <p className="text-gray-600 mb-4">We may share your information with:</p>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">3.1 Service Providers</h3>
            <ul className="list-disc pl-5 mb-4 text-gray-600 space-y-1">
              <li>AI processing providers for image generation</li>
              <li>Print-on-demand partners to fabricate your products</li>
              <li>Shipping/logistics providers</li>
              <li>Payment processors</li>
              <li>Analytics and cloud hosting providers</li>
            </ul>
            <p className="text-gray-600 mb-6">
              These partners access data only as needed to perform services on our behalf.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">3.2 Legal Requirements</h3>
            <p className="text-gray-600 mb-2">
              We may disclose information if required to comply with applicable law or to protect our rights.
            </p>
            <p className="text-gray-600 mb-4">
              We do not sell, rent, or trade personal information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. How Long We Keep Your Data</h2>
            <p className="text-gray-600 mb-4">
              Uploaded photos may be deleted automatically after processing unless you save them in your account.
            </p>
            <p className="text-gray-600 mb-4">
              Order data is retained as needed for bookkeeping, legal, and logistical purposes.
            </p>
            <p className="text-gray-600 mb-4">
              You may request deletion at any time (see Section 8).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Security</h2>
            <p className="text-gray-600 mb-4">
              We use industry-standard security technologies, encryption, and access controls to protect your information.
            </p>
            <p className="text-gray-600 mb-4">
              However, no online transmission is 100% secure, and we cannot guarantee absolute protection.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Return Policy</h2>
            <p className="text-gray-600 mb-4">
              Since every product is personalized and made-to-order, we cannot provide a refund once an order is placed.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. International Data Transfers</h2>
            <p className="text-gray-600 mb-4">
              Your information may be transferred to servers located in other countries.
            </p>
            <p className="text-gray-600 mb-4">
              We ensure such transfers comply with applicable data-protection laws (e.g., GDPR).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Your Rights</h2>
            <p className="text-gray-600 mb-2">Depending on your jurisdiction, you may have rights to:</p>
            <ul className="list-disc pl-5 mb-4 text-gray-600 space-y-1">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion</li>
              <li>Opt out of marketing</li>
              <li>Withdraw consent for photo storage or processing</li>
              <li>Request data portability</li>
            </ul>
            <p className="text-gray-600 mb-4">
              To exercise any of these rights, contact us at [Insert Contact Email].
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Third-Party Links</h2>
            <p className="text-gray-600 mb-4">
              The App may contain links to external websites.
            </p>
            <p className="text-gray-600 mb-4">
              We are not responsible for their privacy practices.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Updates to This Policy</h2>
            <p className="text-gray-600 mb-4">
              We may update this Privacy Policy from time to time.
            </p>
            <p className="text-gray-600 mb-4">
              Changes take effect immediately when published.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact Us</h2>
            <p className="text-gray-600 mb-4">
              If you have any questions or requests regarding this Privacy Policy, please contact us:
            </p>
            <div className="text-gray-600">
              <p className="font-semibold">Atelier Persopol OÜ</p>
              <p>Email: yuyang.jiang@atelier-persopol.com</p>
              <p>Website: <a href="https://atelier-persopol.com" className="text-blue-600 hover:underline">https://atelier-persopol.com</a></p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

